import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { UsersApi, UsersQueries } from '@frontend/api-users';
import { authnClient } from '@frontend/auth';
import { setLoginData } from '@frontend/auth-helpers';
import { useRemoveAppLoader } from '@frontend/document';
import { Trans, useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { UsersTypes } from '@frontend/user-helpers';
import { theme } from '@frontend/theme';
import {
  ContentLoader,
  EmailField,
  FormRow,
  Heading,
  PasswordField,
  PhoneField,
  PrimaryButton,
  SpinningLoader,
  Text,
  TextField,
  TextLink,
  useModalControl,
  WeaveLogoIcon,
  useAlert,
  Button,
} from '@frontend/design-system';
import { PasswordResetModal } from '../components/password-reset-modal';
import { PasswordError } from '../components/user-create/password-error.component';
import { PasswordRequirements } from '../components/user-create/password-requirements';
import { useCreateUserForm } from '../components/user-forms';
import { useValidatePassword } from '../hooks/use-validate-password';

type Props = {
  locationId: string;
  nonceToken: string;
  onSuccess: () => void;
};
export const CreateUser = ({ locationId, nonceToken, onSuccess }: Props) => {
  const alerts = useAlert();
  const { t } = useTranslation('account');
  const resetPasswordModal = useModalControl();
  const [isBegun, setIsBegun] = useState(false);

  useRemoveAppLoader();

  const { formProps, getFieldProps, seedValues, isComplete, reset } = useCreateUserForm({
    onSubmit: (returnValues) => {
      if (!data) return;
      mutation.mutate({
        Nonce: data.Nonce,
        Password: returnValues.password!,
        Profile: {
          FirstName: returnValues.firstName,
          LastName: returnValues.lastName,
          MobileNumber: returnValues.mobileNumber,
        },
      });
    },
  });

  const passwordValidations = useValidatePassword({
    locationId,
    firstName: getFieldProps('firstName').value,
    lastName: getFieldProps('lastName').value,
    passwordValue: getFieldProps('password').value,
    userEmail: getFieldProps('userName').value,
  });

  const { data, isLoading, isSuccess, isError, error } = UsersQueries.useGetCreateUserData(
    { locationId, nonceToken },
    {
      enabled: isBegun,
    }
  );

  const mutation = useMutation({
    mutationFn: (payload: UsersTypes.PutUserPayload) => UsersApi.update.userNonce(payload),
    onError: (e) => {
      alerts.error(t('Something went wrong') + ': ' + e);
    },
    onSuccess: async () => {
      alerts.success(t('Successfully created account.'));
      //set the last location id to this location
      setLoginData(data?.UserID ?? '*', { latestLocationId: locationId });
      await authnClient.signOut();
    },
  });

  const handleCloseModal = (state: 'cancel' | 'submit') => {
    resetPasswordModal.closeModal();
    if (state === 'submit') authnClient.signOut();
  };

  /* Handling query callbacks */
  useEffect(() => {
    if (!!data) {
      seedValues({
        firstName: data.FirstName,
        lastName: data.LastName,
        userName: data.Username,
        mobileNumber: data.MobileNumber,
        password: '',
      });
    }

    if (isError) alerts.error(t(`Error getting users: {{error}}`, { error }));

    return () => reset();
  }, [data, isError]);

  return (
    <main css={createUserStyle(isSuccess ? 'initial' : 'center')}>
      <WeaveLogoIcon size={52} css={{ margin: theme.spacing(10, 0, 1, 0) }} />
      {isLoading && <ContentLoader show={true} message={t('loading profile')} />}
      {isError && (
        <>
          <PasswordResetModal onClose={handleCloseModal} modalProps={resetPasswordModal.modalProps} />
          <Heading
            css={css`
              margin: ${theme.spacing(1, 0)};
            `}
            textAlign='center'
          >
            {t('This link has already been used, or expired')}
          </Heading>
          <PrimaryButton
            css={css`
              margin: ${theme.spacing(1, 0)};
              min-width: 200px;
            `}
            onClick={resetPasswordModal.openModal}
          >
            {t('Get A New Link')}
          </PrimaryButton>

          <Trans t={t}>
            <Text
              css={css`
                margin: ${theme.spacing(1, 0)};
              `}
              textAlign='center'
            >
              Or click <TextLink onClick={() => onSuccess}>{t('here ')}</TextLink> to sign in
            </Text>
          </Trans>
        </>
      )}
      {data && !isError && (
        <>
          <Heading
            css={css`
              margin-bottom: ${theme.spacing(5)};
            `}
          >
            {t('Please finish setting up your account')}
          </Heading>
          <form {...formProps}>
            <div css={formSection}>
              <Heading
                level={2}
                css={css`
                  color: ${theme.colors.neutral90};
                  margin-bottom: ${theme.spacing(4)};
                `}
              >
                {t('Personal Info')}
              </Heading>
              <FormRow>
                <TextField {...getFieldProps('firstName')} label={t('First Name')} />
              </FormRow>
              <FormRow>
                <TextField {...getFieldProps('lastName')} label={t('Last Name')} />
              </FormRow>
              <FormRow>
                <EmailField {...getFieldProps('userName')} label={t('Email Address')} disabled />
              </FormRow>
              <FormRow>
                <PhoneField {...getFieldProps('mobileNumber')} label={t('Mobile Number')} />
              </FormRow>
              <Text
                size='small'
                color='light'
                css={css`
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: 24px;
                `}
              >
                {t(
                  'After you click save, you will receive a text to verify your mobile number for password reset purposes'
                )}
              </Text>
            </div>

            <div css={formSection}>
              <Heading
                level={2}
                css={css`
                  color: ${theme.colors.neutral90};
                  margin-bottom: ${theme.spacing(4)};
                `}
              >
                {t('Create a Password')}
              </Heading>

              <FormRow>
                <PasswordField {...getFieldProps('password')} label={t('Password')} />
              </FormRow>
              <PasswordRequirements {...passwordValidations} />
              {passwordValidations.isPwned && <PasswordError />}
            </div>
            {mutation.isLoading ? (
              <SpinningLoader />
            ) : (
              <Button
                variant='primary'
                disabled={!isComplete || !passwordValidations.isSecure || passwordValidations.isCheckingSecurePW}
                type='submit'
                css={css`
                  width: ${theme.spacing(25)};
                `}
              >
                {t('Save')}
              </Button>
            )}
          </form>
        </>
      )}
      {!isBegun && (
        <>
          <Text
            css={css`
              margin: ${theme.spacing(1, 0)};
            `}
            textAlign='center'
          >
            {t('Click the button below to begin setting up your account')}
          </Text>
          <Button variant='primary' onClick={() => setIsBegun(true)}>
            {t('Begin')}
          </Button>
        </>
      )}
    </main>
  );
};

const createUserStyle = (alignment: 'initial' | 'center') => css`
  max-width: 400px;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: ${alignment};
  margin: 0 auto;
  form {
    width: 100%;
    text-align: left;
  }
`;

const formSection = css`
  margin-bottom: ${theme.spacing(5)};
  h1 {
    color: ${theme.colors.primary50};
    margin-bottom: ${theme.spacing(4)};
  }
`;
